import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';
import { aiLogger } from '../utils/logger';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: number;
  functionCall?: {
    name: string;
    arguments: any;
    result: any;
  };
}

export interface SendMessageResponse {
  message: string;
  functionCall?: {
    name: string;
    arguments: any;
    result: any;
  };
}

class AIService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = `${API_URL}/api/ai`;
  }

  /**
   * Send a message to the AI assistant
   */
  async sendMessage(messages: ChatMessage[]): Promise<SendMessageResponse> {
    try {
      const token = await getAuthToken();

      if (!token) {
        throw new Error('Authentication token not found. Please log in.');
      }

      // Build messages array with function results properly formatted
      const formattedMessages: any[] = [];

      for (const msg of messages) {
        // Add the main message
        if (msg.functionCall && msg.functionCall.result) {
          // This is an assistant message that called a function
          formattedMessages.push({
            role: msg.role,
            content: msg.content || null,
            function_call: {
              name: msg.functionCall.name,
              arguments: JSON.stringify(msg.functionCall.arguments),
            },
          });

          // Add the function result as a separate message with role "function"
          formattedMessages.push({
            role: 'function',
            name: msg.functionCall.name,
            content: JSON.stringify(msg.functionCall.result),
          });
        } else {
          // Regular message without function call
          formattedMessages.push({
            role: msg.role,
            content: msg.content,
          });
        }
      }

      // Add timeout to prevent infinite hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 120 second timeout for AI operations (OpenAI can be slow)

      const response = await fetch(`${this.apiUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: formattedMessages,
          enableFunctions: true,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to send message: ${response.statusText}`
        );
      }

      const data = await response.json();

      // Validate response data
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from AI service');
      }

      // Check if backend returned an error response
      if (data.success === false && data.error) {
        throw new Error(data.error);
      }

      // Ensure we have a message field
      if (!data.message || typeof data.message !== 'string' || data.message.trim() === '') {
        aiLogger.warn('AI response missing or empty message field, using default');
        data.message = 'I understand. How can I help you?';
      }

      aiLogger.debug('AI service returning data with message:', data.message?.substring(0, 50));
      return data;
    } catch (error: any) {
      aiLogger.error('AI Service - Send Message Error:', error);

      // Handle specific error types
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Orion is taking too long to respond. Please try again.');
      }

      throw new Error(
        error.message || 'Failed to communicate with AI assistant. Please try again.'
      );
    }
  }

  /**
   * Transcribe audio to text (Speech-to-Text)
   */
  async transcribeAudio(audioUri: string): Promise<string> {
    try {
      const token = await getAuthToken();

      if (!token) {
        throw new Error('Authentication token not found. Please log in.');
      }

      const formData = new FormData();
      formData.append('audio', {
        uri: audioUri,
        type: 'audio/mp4',
        name: 'recording.mp4',
      } as any);

      const response = await fetch(`${this.apiUrl}/voice/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to transcribe audio: ${response.statusText}`
        );
      }

      const data = await response.json();
      return data.text;
    } catch (error: any) {
      aiLogger.error('AI Service - Transcribe Audio Error:', error);
      throw new Error(
        error.message || 'Failed to transcribe audio. Please try again.'
      );
    }
  }

  /**
   * Convert text to speech (Text-to-Speech)
   */
  async textToSpeech(text: string, voice: string = 'nova'): Promise<string> {
    try {
      const token = await getAuthToken();

      if (!token) {
        throw new Error('Authentication token not found. Please log in.');
      }

      const response = await fetch(`${this.apiUrl}/voice/synthesize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ text, voice }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to synthesize speech: ${response.statusText}`
        );
      }

      // Get the audio blob and convert to base64 or save locally
      const blob = await response.blob();
      // For now, return a placeholder - you'll need to handle audio playback
      // based on your audio library (react-native-sound, expo-av, etc.)
      return 'audio-data'; // TODO: Implement audio handling
    } catch (error: any) {
      aiLogger.error('AI Service - Text to Speech Error:', error);
      throw new Error(
        error.message || 'Failed to generate speech. Please try again.'
      );
    }
  }

  /**
   * Test connection to AI service
   */
  async testConnection(): Promise<boolean> {
    try {
      const token = await getAuthToken();

      if (!token) {
        return false;
      }

      const response = await fetch(`${this.apiUrl}/test`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      return response.ok;
    } catch (error) {
      aiLogger.error('AI Service - Test Connection Error:', error);
      return false;
    }
  }

  /**
   * Confirm and save an entry (called by frontend when user clicks Confirm in preview modal)
   */
  async confirmSaveEntry(taskKey: string): Promise<{success: boolean; data?: any; error?: string}> {
    try {
      const token = await getAuthToken();

      if (!token) {
        throw new Error('Authentication token not found. Please log in.');
      }

      aiLogger.debug(`[AIService] confirmSaveEntry: ${taskKey}`);

      const response = await fetch(`${this.apiUrl}/confirm-save-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ taskKey }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      aiLogger.debug(`[AIService] confirmSaveEntry result:`, result);

      return result;
    } catch (error) {
      aiLogger.error('AI Service - confirmSaveEntry Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save entry',
      };
    }
  }
}

export default new AIService();
